import { useState } from "react";
import { Check, Copy, Download, FileText, Quote, Sparkles, Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  pullEvidenceForClaim,
  type EssayToOutline,
  type PullEvidenceResult,
} from "@/lib/smartessay.functions";

function outlineToMarkdown(o: EssayToOutline, title?: string): string {
  const lines: string[] = [];
  lines.push(`# Outline${title ? `: ${title}` : ""}`);
  lines.push("");
  lines.push(`**Thesis:** ${o.thesis}`);
  lines.push("");
  lines.push("## Introduction");
  lines.push(`- **Hook:** ${o.introduction.hook}`);
  lines.push(`- **Context:** ${o.introduction.context}`);
  lines.push(`- **Thesis statement:** ${o.introduction.thesis_statement}`);
  lines.push("");
  o.body_paragraphs.forEach((p) => {
    lines.push(`## Body Paragraph ${p.paragraph_index}`);
    lines.push(`- **Topic sentence:** ${p.topic_sentence}`);
    if (p.main_points.length) {
      lines.push(`- **Main points:**`);
      p.main_points.forEach((m) => lines.push(`  - ${m}`));
    }
    if (p.evidence.length) {
      lines.push(`- **Evidence:**`);
      p.evidence.forEach((e) => lines.push(`  - ${e}`));
    }
    lines.push(`- **Link to thesis:** ${p.link_to_thesis}`);
    lines.push("");
  });
  lines.push("## Conclusion");
  lines.push(`- **Restated thesis:** ${o.conclusion.restated_thesis}`);
  lines.push(`- **Synthesis:** ${o.conclusion.synthesis}`);
  lines.push(`- **Closing thought:** ${o.conclusion.closing_thought}`);
  if (o.notes.length) {
    lines.push("");
    lines.push("## Notes");
    o.notes.forEach((n) => lines.push(`- ${n}`));
  }
  return lines.join("\n");
}

type EvidenceState = {
  loading: boolean;
  result: PullEvidenceResult | null;
};

export function EssayOutlineView({
  outline,
  title,
  sourceText,
  citationStyle = "MLA",
}: {
  outline: EssayToOutline;
  title?: string;
  sourceText?: string;
  citationStyle?: "MLA" | "APA";
}) {
  const [copied, setCopied] = useState(false);
  const pullEvidence = useServerFn(pullEvidenceForClaim);
  // Key = `${paragraphIndex}:${pointIndex}` where pointIndex = -1 for the topic sentence
  const [evidence, setEvidence] = useState<Record<string, EvidenceState>>({});

  const md = outlineToMarkdown(outline, title);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(md);
      setCopied(true);
      toast.success("Outline copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  }

  function onDownload() {
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title || "essay-outline").replace(/[^a-z0-9\-_]+/gi, "-")}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function onPull(key: string, claim: string) {
    if (!title?.trim() && !sourceText?.trim()) {
      toast.error("Add a source title (or upload source text) above so we can search for evidence.");
      return;
    }
    setEvidence((s) => ({ ...s, [key]: { loading: true, result: s[key]?.result ?? null } }));
    try {
      const res = await pullEvidence({
        data: {
          claim,
          title: title?.trim() || undefined,
          source_text: sourceText?.trim() || undefined,
          citation_style: citationStyle,
          max_quotes: 3,
        },
      });
      setEvidence((s) => ({ ...s, [key]: { loading: false, result: res } }));
    } catch (err) {
      setEvidence((s) => ({ ...s, [key]: { loading: false, result: s[key]?.result ?? null } }));
      toast.error((err as Error).message || "Couldn't pull evidence.");
    }
  }

  const canPull = Boolean(title?.trim() || sourceText?.trim());

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <h2 className="font-display text-lg font-semibold">Your essay as an outline</h2>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onCopy}>
            {copied ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onDownload}>
            <Download className="mr-1.5 h-3.5 w-3.5" /> Download .md
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-muted/30 p-4">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Thesis</div>
        <p className="mt-1 text-sm font-medium">{outline.thesis}</p>
      </div>

      {!canPull && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-sun/40 bg-sun/10 p-3 text-xs text-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sun" />
          <span>
            Add the source <strong>title</strong> (or upload the source text) above to unlock per-claim
            evidence pulling.
          </span>
        </div>
      )}

      <Section heading="Introduction">
        <Row label="Hook" value={outline.introduction.hook} />
        <Row label="Context" value={outline.introduction.context} />
        <Row label="Thesis statement" value={outline.introduction.thesis_statement} />
      </Section>

      {outline.body_paragraphs.map((p) => {
        const topicKey = `${p.paragraph_index}:-1`;
        return (
          <Section key={p.paragraph_index} heading={`Body Paragraph ${p.paragraph_index}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <Row label="Topic sentence" value={p.topic_sentence} />
              <PullButton
                disabled={!canPull}
                loading={evidence[topicKey]?.loading}
                hasResult={Boolean(evidence[topicKey]?.result)}
                onClick={() => onPull(topicKey, p.topic_sentence)}
              />
            </div>
            <EvidencePanel state={evidence[topicKey]} claim={p.topic_sentence} />

            {p.main_points.length > 0 && (
              <div className="text-sm">
                <div className="font-medium">Main points:</div>
                <ul className="mt-1 space-y-2 pl-1">
                  {p.main_points.map((point, idx) => {
                    const key = `${p.paragraph_index}:${idx}`;
                    return (
                      <li key={idx} className="rounded-md border border-border/60 bg-muted/20 p-2">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <span className="text-sm text-muted-foreground">• {point}</span>
                          <PullButton
                            disabled={!canPull}
                            loading={evidence[key]?.loading}
                            hasResult={Boolean(evidence[key]?.result)}
                            onClick={() => onPull(key, point)}
                          />
                        </div>
                        <EvidencePanel state={evidence[key]} claim={point} />
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {p.evidence.length > 0 && <ListRow label="Evidence already used" items={p.evidence} />}
            <Row label="Link to thesis" value={p.link_to_thesis} muted />
          </Section>
        );
      })}

      <Section heading="Conclusion">
        <Row label="Restated thesis" value={outline.conclusion.restated_thesis} />
        <Row label="Synthesis" value={outline.conclusion.synthesis} />
        <Row label="Closing thought" value={outline.conclusion.closing_thought} />
      </Section>

      {outline.notes.length > 0 && (
        <Section heading="Structural notes">
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {outline.notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function PullButton({
  onClick,
  loading,
  disabled,
  hasResult,
}: {
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  hasResult?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={disabled || loading}
      onClick={onClick}
      className="shrink-0 gap-1.5"
      title={disabled ? "Add a source title above first" : "Pull evidence that directly supports this claim"}
    >
      {loading ? (
        <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Pulling…</>
      ) : (
        <><Sparkles className="h-3.5 w-3.5" /> {hasResult ? "Pull again" : "Pull supporting evidence"}</>
      )}
    </Button>
  );
}

function EvidencePanel({ state, claim }: { state?: EvidenceState; claim: string }) {
  if (!state || (!state.result && !state.loading)) return null;
  const r = state.result;
  if (!r) return null;

  const strong = r.quotes.filter((q) => q.match_strength === "strong");
  const moderate = r.quotes.filter((q) => q.match_strength !== "strong");

  return (
    <div className="mt-3 rounded-xl border-2 border-primary/25 bg-primary/[0.04] p-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
          Evidence for claim
        </p>
      </div>
      <p className="mt-1 text-sm italic text-foreground">"{claim}"</p>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Source:{" "}
        {r.source_mode === "uploaded_source"
          ? "your uploaded source"
          : r.source_mode === "ai_memory"
            ? "AI's knowledge of the named work (verify against your copy)"
            : "no source available"}
      </p>

      {r.quotes.length === 0 ? (
        <div className="mt-3 rounded-lg border border-sun/40 bg-sun/10 p-3 text-sm">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground">
            <AlertTriangle className="h-3.5 w-3.5 text-sun" /> No strong evidence found
          </div>
          {r.no_match_message && <p className="mt-1.5">{r.no_match_message}</p>}
          {r.revision_suggestion && (
            <p className="mt-1.5 text-muted-foreground">
              <span className="font-medium text-foreground">Try this:</span> {r.revision_suggestion}
            </p>
          )}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {strong.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-mint">
                Strong matches ({strong.length})
              </p>
              {strong.map((q, i) => (
                <QuoteCard key={`s${i}`} q={q} />
              ))}
            </div>
          )}
          {moderate.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-sun">
                Moderate matches ({moderate.length}) — needs a connecting sentence
              </p>
              {moderate.map((q, i) => (
                <QuoteCard key={`m${i}`} q={q} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function QuoteCard({
  q,
}: {
  q: {
    quote: string;
    citation: string;
    why_supports: string;
    confidence: "high" | "medium" | "low";
    match_strength: "strong" | "moderate";
  };
}) {
  return (
    <div className="rounded-lg border border-border bg-background p-3 shadow-soft">
      <blockquote className="flex gap-2 border-l-2 border-primary pl-2 text-sm italic">
        <Quote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
        <span>
          "{q.quote}" <span className="not-italic text-muted-foreground">{q.citation}</span>
        </span>
      </blockquote>
      <p className="mt-2 text-xs text-foreground">
        <span className="font-semibold">Why this supports the claim: </span>
        {q.why_supports}
      </p>
      <div className="mt-1.5 flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
        <span>Confidence: {q.confidence}</span>
        <span>·</span>
        <span>Match: {q.match_strength}</span>
      </div>
    </div>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div className="mt-5 border-t border-border pt-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-primary">{heading}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="text-sm">
      <span className="font-medium">{label}: </span>
      <span className={muted ? "text-muted-foreground" : ""}>{value}</span>
    </div>
  );
}

function ListRow({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="text-sm">
      <div className="font-medium">{label}:</div>
      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted-foreground">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  );
}
